import { useMutation, useQuery } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Button } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";

import SAVE_CONCLUSION_VIRTUAL, { adaptAddVisitaConclusionVirtualInput } from "../../../../../../data/graphQL/mutations/Visitas/saveConclusionVirtual";
import { useStepSave } from "../../../../hooks/useStepSave";
import { adaptAddVisitaInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisita";
import { useNavigate } from "react-router-dom";
import { getUncompletedRequiredFieldsValidacion } from "../../../../../../utils/visitaValidation";
import toast from "react-hot-toast";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_COMBO_VISITA_VALOR, adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import moment from "moment";
import { obtenerCoordenadas } from "../../../../constants/getUbicacion";
import { useValidacionDiscrepanciaHorariaVisita } from "../../../../hooks/useValidacionDiscrepanciaHorariaVisita";

const ID_COMBO_INTERLOCUTOR = 5;
const ID_INTERLOCUTOR_PACIENTE = 10;

export default function ConclusionVirtual({ readOnly = false, onExposeFormatted, exposedFormattedByStep, onSaveRef }) {
  const navigate = useNavigate();
  const { validarDiscrepancia, modal: modalDiscrepancia } = useValidacionDiscrepanciaHorariaVisita();

  const [formData, setFormData] = useState({});
  const [finalizarLoading, setFinalizarLoading] = useState(false);
  const [interlocutorOptions, setInterlocutorOptions] = useState([]);

  const { visita, setVisita } = useVisita();

  const [save] = useMutation(SAVE_CONCLUSION_VIRTUAL);

  const { data: dataComboVisitaValor } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_INTERLOCUTOR }
  });

  useEffect(() => {
    if (!dataComboVisitaValor) return;
    setInterlocutorOptions(adaptCombosVisitas(dataComboVisitaValor));
  }, [dataComboVisitaValor]);

  const formattedInputs = useMemo(() => {
    return conclusionVirtualInputs.map((input) => {
      const cloned = { ...input };
      if (readOnly) cloned.disabled = true;

      switch (input.name) {
        case "idComboVisitaValorInterlocutor":
          cloned.options = interlocutorOptions;
          break;
        default:
          break;
      }

      return cloned;
    });
  }, [readOnly, formData.fechaVisita, interlocutorOptions]);

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'idComboVisitaValorInterlocutor')
      setFormData((prev) => ({ ...prev, nombreCompleto: ID_INTERLOCUTOR_PACIENTE == value ? visita?.paciente : null }));
  }, []);

  const saveVisitaConclusion = async (visitaData, finalizar) => {

    const fechaInicio = moment(visita.fechaVisita);
    const fechaFin = moment(visitaData.fechaFin);

    if (!fechaFin.isValid()) {
      toast.error("La fecha de finalización es inválida.");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    if (fechaFin.isSameOrBefore(fechaInicio, 'minute')) {
      toast.error("La fecha de finalización debe ser posterior a la fecha de inicio de la visita.");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    if (fechaFin.isAfter(moment())) {
      toast.error("La fecha de finalización no puede ser mayor a la fecha y hora actual");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    const coordenadas = await obtenerCoordenadas();

    const resValidacionDiscrepancia = await validarDiscrepancia(visitaData.idVisita, visitaData.fechaVisita, visitaData.fechaFin);
    if (!resValidacionDiscrepancia.permitir) return;

    const { data } = await save({
      variables: { input: adaptAddVisitaConclusionVirtualInput(visitaData, finalizar, coordenadas?.latitud, coordenadas?.longitud) }
    });

    const id = data?.saveVisitaConclusionVirtual?.idVisita;
    if (!id) throw new Error("Error al guardar la visita");

    return id;
  };

  useStepSave(onSaveRef, async () => {
    const updatedVisita = { ...visita, ...formData };
    await saveVisitaConclusion(updatedVisita);
    setVisita(updatedVisita);
  });

  async function tieneGeoHabilitada() {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        resolve(false);
        return;
      }

      const timeoutMs = 60000;

      // Intenta obtener la ubicación con un timeout
      const timeoutId = setTimeout(() => {
        resolve(false);
      }, timeoutMs);

      navigator.geolocation.getCurrentPosition(
        () => {
          clearTimeout(timeoutId);
          resolve(true);
        },
        (error) => {
          clearTimeout(timeoutId);
          // Si el error es PERMISSION_DENIED, no tiene permisos
          // Si es POSITION_UNAVAILABLE o TIMEOUT, puede tener permisos pero hay otros problemas
          if (error.code === error.PERMISSION_DENIED) {
            resolve(false);
          } else {
            // Asumimos que tiene permisos pero hay problemas técnicos
            resolve(true);
          }
        },
        {
          timeout: timeoutMs,
          maximumAge: 0,
          enableHighAccuracy: false // Más rápido para la verificación
        }
      );
    });
  }

  const handleGuardar = async () => {
    try {
      const tieneGeo = await tieneGeoHabilitada();

      if (!tieneGeo) {
        toast.error("Debe habilitar el permiso de ubicación en su navegador para crear una nueva visita.", {
          duration: 4000
        });
        return;
      }

      setFinalizarLoading(true);


      validateStepFields({ formattedInputs, formData });

      const updatedVisita = { ...visita, ...formData };
      await saveVisitaConclusion(updatedVisita, true);
      setVisita(updatedVisita);

      toast.success("Visita guardada correctamente.");
      navigate("/misVisitas");
    } catch (error) {
      console.error(error);
    } finally {
      setFinalizarLoading(false);
    }
  };

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'conclusionVirtual',
      stepName: 'Conclusión',
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.map((input) =>
          <Grid size={{ xs: 12 }} key={input.name}>
            <RenderInputByTypeMobile
              key={`${input.name} - ${input.name === 'nombreCompleto' ? formData['idComboVisitaValorInterlocutor'] : ''}`}
              input={input}
              label={input.displayName}
              value={formData[input.name]}
              handleChange={handleChange}
              row={input.type === "multiple_boolean"} />

          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGuardar}
            disabled={readOnly}
            fullWidth
            loading={finalizarLoading}>

            Finalizar
          </Button>
        </Grid>
      </Grid>
      {modalDiscrepancia}
    </CardBox>);
}

const conclusionVirtualInputs = [
  {
    name: "evolucion",
    displayName: "Evolución",
    type: "long_string",
    maxLength: 4000,
    required: true
  },
  {
    name: "codigoAtencion",
    displayName: "Código de Atención",
    type: "long_string",
    maxLength: 500,
    required: true
  },
  {
    name: "fechaFin",
    displayName: "Fecha de finalización",
    type: "datetime",
    required: true,
    disableFuture: true
  },
  {
    name: "idComboVisitaValorInterlocutor",
    displayName: "Interlocutor",
    type: "select",
    required: true
  },
  {
    name: "nombreCompleto",
    displayName: "Nombre",
    type: "string",
    maxLength: 250,
    required: true
  },
  {
    name: "dNI",
    displayName: "DNI",
    type: "string",
    maxLength: 50,
    required: false
  },
  {
    name: "email",
    displayName: "Email paciente / Interlocutor",
    type: "string",
    maxLength: 250,
    required: false
  }];